/**
 * Справочник модели: полное описание входов, параметров и результата.
 *
 * Показывается до формы «Try it», чтобы разработчик сначала прочитал, что
 * принимает модель и с какими ограничениями, и только потом запускал.
 */
import { useI18n } from "../../i18n/I18nProvider";
import { Badge, Card, InlineCode, Table, typographyClass } from "../../ui-kit";
import { resolveField } from "./fieldText";
import type { FieldSpec, ModelDetail, OutputSpec } from "../../api/types";

/** Тип поля в терминах API, а не UI-контрола. */
function typeLabel(field: FieldSpec): string {
  if (field.data_type === "array") return `${field.item_type ?? "any"}[]`;
  return field.data_type;
}

/** Ограничения одной строкой: диапазон, шаг, длина массива, формат файла. */
function constraints(field: FieldSpec, hidden: number, t: (key: string, vars?: Record<string, string | number>) => string): string[] {
  const parts: string[] = [];

  if (field.range) {
    const { min, max, step } = field.range;
    if (min != null || max != null) parts.push(`${min ?? "−∞"} … ${max ?? "∞"}`);
    if (step != null) parts.push(`${t("ref.step")} ${step}`);
    if (field.range.random) parts.push(t("ref.random"));
  }
  if (field.max_items != null) parts.push(`${t("ref.maxItems")} ${field.max_items}`);
  if (field.accept?.length) parts.push(field.accept.map((ext) => `.${ext}`).join(", "));
  if (hidden > 0) parts.push(t("ref.restricted", { count: hidden }));

  return parts;
}

function FieldTable({ model, fields, title }: { model: ModelDetail; fields: FieldSpec[]; title: string }) {
  const { t, fieldDoc } = useI18n();
  if (fields.length === 0) return null;

  return (
    <>
      <h2 className={typographyClass("header-16-semibold")}>{title}</h2>
      <Table
        rows={fields}
        rowKey={(field) => `${field.kind}-${field.name}`}
        columns={[
          {
            key: "name",
            header: t("ref.field"),
            render: (field) => {
              const resolved = resolveField(field, fieldDoc(model.id_global, field.kind, field.name));
              return (
                <span style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-1)", alignItems: "flex-start" }}>
                  <InlineCode>{field.name}</InlineCode>
                  {/* Отступ равен горизонтальному паддингу InlineCode, поэтому подпись
                      встаёт ровно под текстом кода, а не под краем его подложки. */}
                  <span
                    className={typographyClass("caption-11-medium")}
                    style={{ color: "var(--color-text-secondary-v2)", paddingLeft: "var(--spacing-1)" }}
                  >
                    {resolved.label}
                  </span>
                </span>
              );
            },
          },
          {
            key: "type",
            header: t("ref.type"),
            render: (field) => (
              <span style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-1)", alignItems: "flex-start" }}>
                <Badge tone="grey" size="small">{typeLabel(field)}</Badge>
                <Badge tone={field.required ? "error" : "outline"} size="small">
                  {field.required ? t("model.required") : t("model.optional")}
                </Badge>
              </span>
            ),
          },
          {
            key: "description",
            header: t("ref.meaning"),
            render: (field) => {
              const resolved = resolveField(field, fieldDoc(model.id_global, field.kind, field.name));
              const limits = constraints(field, resolved.hiddenOptions.length, t);
              return (
                <span style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-2)" }}>
                  <span>{resolved.description ?? "—"}</span>

                  {resolved.options.length > 0 && (
                    <span style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-1)" }}>
                      {resolved.options.map((option) => (
                        <Badge key={option.value} tone="default" size="small">
                          {option.label === option.value ? option.value : `${option.label} · ${option.value}`}
                        </Badge>
                      ))}
                    </span>
                  )}

                  {limits.length > 0 && (
                    <span className={typographyClass("caption-11-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
                      {limits.join(" · ")}
                    </span>
                  )}
                </span>
              );
            },
          },
          {
            key: "default",
            header: t("ref.default"),
            render: (field) =>
              field.default === null || field.default === undefined || field.default === "" ? (
                <span style={{ color: "var(--color-text-tertiary)" }}>—</span>
              ) : (
                <InlineCode>{JSON.stringify(field.default)}</InlineCode>
              ),
          },
        ]}
      />
    </>
  );
}

function OutputTable({ model, outputs }: { model: ModelDetail; outputs: OutputSpec[] }) {
  const { t, fieldDoc } = useI18n();
  if (outputs.length === 0) return null;

  return (
    <>
      <h2 className={typographyClass("header-16-semibold")}>{t("model.outputs")}</h2>
      <Table
        rows={outputs}
        rowKey={(output) => output.name}
        columns={[
          { key: "name", header: t("ref.field"), render: (output) => <InlineCode>{output.name}</InlineCode> },
          {
            key: "type",
            header: t("ref.type"),
            render: (output) => <Badge tone="grey" size="small">{output.data_type}</Badge>,
          },
          {
            key: "description",
            header: t("ref.meaning"),
            render: (output) =>
              fieldDoc(model.id_global, "output", output.name).description ?? t("ref.outputDefault"),
          },
        ]}
      />
      <p className={typographyClass("caption-12-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
        {t("ref.outputNote")}
      </p>
    </>
  );
}

export function ModelReference({ model }: { model: ModelDetail }) {
  const { t } = useI18n();

  return (
    <Card>
      <div>
        <h1 className={typographyClass("header-20-semibold")}>{t("ref.title")}</h1>
        <p className={typographyClass("caption-13-medium")} style={{ color: "var(--color-text-secondary-v2)", marginTop: "var(--spacing-1)" }}>
          {t("ref.subtitle")}
        </p>
      </div>

      <FieldTable model={model} fields={model.inputs} title={t("model.inputs")} />
      <FieldTable model={model} fields={model.params} title={t("model.params")} />
      <OutputTable model={model} outputs={model.output_specs} />
    </Card>
  );
}
